import React from "react";
import { showTextViewer } from "../../services/text-viewer-service";
import "./json-cell-renderer.scss";

export interface TextCellRendererProps {
  /** the cell's text; null is NULL, which is not the empty string */
  value: string | null;
  language: string;
  title?: string;
  /** Given, the cell opens the viewer as an editor and Apply hands the new text here. */
  onApply?: (text: string) => void;
  /** What the cell shows instead of the text's first line (a JSON value on one line). */
  preview?: string;
  /** Given, the editor also offers "Set to NULL". */
  onSetNull?: () => void;
}

const PREVIEW_LENGTH = 60;

/**
 * Cell Renderer for a large block of text or code.
 * Displays the first line as a preview, with the line count, and opens the
 * full text in the text viewer modal on click.
 */
export const TextCellRenderer: React.FC<TextCellRendererProps> = ({
  value,
  language,
  title,
  onApply,
  preview: previewText,
  onSetNull,
}) => {
  const text = value ?? "";
  const lineCount = text.split("\n").length;
  const firstLine = previewText ?? text.trimStart().split("\n", 1)[0];
  const preview = firstLine.substring(0, PREVIEW_LENGTH);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    showTextViewer({
      text,
      isNull: value === null,
      language,
      title: title || "Text",
      onApply,
      onSetNull,
    });
  };

  // NULL and the empty string look different: ∅ is NULL, "" is an empty value
  if (value === null || value === "") {
    return (
      <span className="json-cell-renderer">
        <span
          className="json-preview-link"
          onClick={handleClick}
          title={value === null ? "NULL — open in editor" : "Empty string — open in editor"}
        >
          {value === null ? "∅" : '""'} <span className="json-expand-indicator">{onApply ? "edit" : "view"}</span>
        </span>
      </span>
    );
  }

  return (
    <span className="json-cell-renderer">
      <span
        className="json-preview-link"
        onClick={handleClick}
        title={onApply ? "Open in editor" : "Open in viewer"}
      >
        {preview}
        <span className="json-expand-indicator">
          …{lineCount > 1 && previewText === undefined ? ` (${lineCount} lines)` : ""}
        </span>
      </span>
    </span>
  );
};
